import type { FormatDeContenu } from '@taki/sdk';

/**
 * LA SCENE D'UN CROQUIS, telle qu'elle se range dans un contenu du kit
 * (`sdk.contents`, famille `contents`, type `croquis`).
 *
 * CE MODULE N'IMPORTE PAS LE MOTEUR, et c'est voulu : il est lu par le jeu de
 * demonstration (panneau de reglages, donc dans le bundle de chaque client) et
 * par les tests. Un element de scene y est vu de loin (`ElementDeScene`) ; le
 * moteur le restaure en entier a l'ouverture (`restoreElements`).
 *
 * LA CHARGE EST UN JSON EN CLAIR. Le SDK la compresse et la decoupe lui-meme
 * (`saveContent`, au-dela de `SEUIL_EN_CLAIR`) : la #188 compressait elle-meme
 * sous un prefixe maison, ce n'est plus son affaire.
 *
 * LES IMAGES VOYAGENT DANS LA CHARGE, reduites (`images.ts`). Le lot Fichiers
 * du kit (Cloud Storage) n'est pas dans la base de cette app : le jour ou il y
 * est, les images en sortent par une migration de format (1 vers 2), et cette
 * ligne est a reecrire.
 */

/** Le sous-type des contenus de Croquis (porte `contents` / `croquis`, cf. `taki.app.json`). */
export const TYPE_CROQUIS = 'croquis';

/** Duree pendant laquelle un element efface reste dans la scene (voir `elaguer`). */
export const RETENTION_SUPPRIMES_MS = 24 * 60 * 60 * 1000;

/** Un element de scene, vu de loin : le moteur porte le reste. */
export interface ElementDeScene {
  id: string;
  type: string;
  isDeleted?: boolean;
  updated?: number;
  version?: number;
  fileId?: string | null;
  [clef: string]: unknown;
}

/** Une image collee : le moteur la decrit ainsi (`BinaryFileData`). */
export interface FichierDeScene {
  id: string;
  mimeType: string;
  dataURL: string;
  created: number;
}

/** Ce qu'un croquis enregistre. */
export interface SceneCroquis {
  elements: ElementDeScene[];
  /** Le fond de la planche ; `null` : celui du moteur. */
  fond: string | null;
  /** Le pas de la grille quand elle est allumee ; `null` : eteinte. */
  grille: number | null;
  /** Les images, par id de fichier. */
  fichiers: Record<string, FichierDeScene>;
}

export const SCENE_VIDE: SceneCroquis = Object.freeze({
  elements: [],
  fond: null,
  grille: null,
  fichiers: {},
}) as SceneCroquis;

/* ------------------------------------------------------------------ */
/* La validation : une charge mal formee LEVE, elle ne se caste pas     */
/* ------------------------------------------------------------------ */

function estObjet(valeur: unknown): valeur is Record<string, unknown> {
  return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}

function element(brut: unknown, rang: number): ElementDeScene {
  if (!estObjet(brut)) throw new Error(`element ${rang} : objet attendu`);
  if (typeof brut.id !== 'string' || brut.id === '') throw new Error(`element ${rang} : id attendu`);
  if (typeof brut.type !== 'string' || brut.type === '') throw new Error(`element ${rang} : type attendu`);
  if ('isDeleted' in brut && typeof brut.isDeleted !== 'boolean') throw new Error(`element ${rang} : isDeleted booleen attendu`);
  if ('updated' in brut && typeof brut.updated !== 'number') throw new Error(`element ${rang} : updated nombre attendu`);
  if ('fileId' in brut && brut.fileId !== null && typeof brut.fileId !== 'string') {
    throw new Error(`element ${rang} : fileId attendu`);
  }
  return brut as ElementDeScene;
}

function fichier(clef: string, brut: unknown): FichierDeScene {
  if (!estObjet(brut)) throw new Error(`fichier ${clef} : objet attendu`);
  if (brut.id !== clef) throw new Error(`fichier ${clef} : id different de sa clef`);
  if (typeof brut.mimeType !== 'string' || !brut.mimeType.startsWith('image/')) {
    throw new Error(`fichier ${clef} : image attendue`);
  }
  if (typeof brut.dataURL !== 'string' || !brut.dataURL.startsWith('data:image/')) {
    throw new Error(`fichier ${clef} : data URL d'image attendue`);
  }
  const created = typeof brut.created === 'number' ? brut.created : 0;
  return { id: clef, mimeType: brut.mimeType, dataURL: brut.dataURL, created };
}

/** Valide une scene lue (format 1). LEVE sur la premiere faute, avec sa place. */
export function validerScene(brut: unknown): SceneCroquis {
  if (!estObjet(brut)) throw new Error('scene : objet attendu');
  if (!Array.isArray(brut.elements)) throw new Error('scene : elements, tableau attendu');
  const elements = brut.elements.map((e, i) => element(e, i));
  const fond = brut.fond ?? null;
  if (fond !== null && (typeof fond !== 'string' || fond.length > 64)) throw new Error('scene : fond invalide');
  const grille = brut.grille ?? null;
  if (grille !== null && (typeof grille !== 'number' || !Number.isFinite(grille) || grille <= 0)) {
    throw new Error('scene : grille invalide');
  }
  const fichiersBruts = brut.fichiers ?? {};
  if (!estObjet(fichiersBruts)) throw new Error('scene : fichiers, objet attendu');
  const fichiers: Record<string, FichierDeScene> = {};
  for (const [clef, valeur] of Object.entries(fichiersBruts)) fichiers[clef] = fichier(clef, valeur);
  return { elements, fond, grille, fichiers };
}

/**
 * LE FORMAT DE LA CHARGE, version 1 : `{ elements, fond, grille, fichiers }`
 * en JSON. Un texte vide (contenu tout juste ne) est une planche vide.
 * Monter `version` exige sa migration ET un vrai fichier ancien en test
 * (`tests/fixtures/`).
 */
export const FORMAT_CROQUIS: FormatDeContenu<SceneCroquis> = {
  version: 1,
  lire(texte) {
    if (texte === '') return { ...SCENE_VIDE, elements: [], fichiers: {} };
    return validerScene(JSON.parse(texte));
  },
};

/* ------------------------------------------------------------------ */
/* L'ecriture : ce qui part en base                                     */
/* ------------------------------------------------------------------ */

/**
 * ELAGUE LES ELEMENTS EFFACES TROP VIEUX, et les images que plus rien ne cite.
 *
 * Un element efface est GARDE un temps (24 h, son horodatage `updated`) : le
 * moteur distingue « efface ailleurs » de « pas encore arrive » par ces
 * pierres tombales, et un ctrl+Z doit pouvoir rendre une image effacee. Passe
 * ce delai, il part ; et une image que plus aucun element garde ne cite part
 * avec lui, sinon la charge grossirait sans fin d'images supprimees.
 */
export function elaguer(scene: SceneCroquis, maintenant: number = Date.now()): SceneCroquis {
  const elements = scene.elements.filter((e) => {
    if (e.isDeleted !== true) return true;
    const quand = typeof e.updated === 'number' ? e.updated : 0;
    return maintenant - quand < RETENTION_SUPPRIMES_MS;
  });
  const cites = new Set(elements.map((e) => e.fileId).filter((id): id is string => typeof id === 'string'));
  const fichiers: Record<string, FichierDeScene> = {};
  for (const [id, f] of Object.entries(scene.fichiers)) if (cites.has(id)) fichiers[id] = f;
  return { elements, fond: scene.fond, grille: scene.grille, fichiers };
}

/**
 * Le texte enregistre, au format courant. PUR ET DETERMINISTE : le kit compare
 * des textes pour decider (rien de neuf, travail deja en base, brouillon en
 * avance), donc la meme valeur doit toujours donner le meme texte. L'elagage,
 * qui depend de l'heure, se fait AVANT, quand l'editeur signale la scene.
 */
export function ecrireScene(scene: SceneCroquis): string {
  return JSON.stringify({
    elements: scene.elements,
    fond: scene.fond,
    grille: scene.grille,
    fichiers: scene.fichiers,
  });
}

/** Les seuls elements qu'on dessine : ceux qui n'ont pas ete effaces. */
export function elementsVivants(elements: readonly ElementDeScene[]): ElementDeScene[] {
  return elements.filter((e) => e.isDeleted !== true);
}

/**
 * L'EMPREINTE DE CE QUI S'ENREGISTRE, pour ne signaler au kit QUE ce qui
 * change. Le moteur appelle `onChange` a chaque mouvement de souris, une
 * selection ou un defilement compris ; signaler chacun de ces appels ferait
 * dire « Pas encore enregistre » a un croquis qu'on se contente de regarder.
 * `versionDeScene` est la somme des versions des elements (`getSceneVersion`
 * du moteur, passee par l'appelant).
 */
export function empreinte(versionDeScene: number, fond: string | null, grille: number | null, nbFichiers: number): string {
  return `${versionDeScene}|${fond ?? ''}|${grille ?? ''}|${nbFichiers}`;
}
